import { maxBy } from 'es-toolkit';
import { EllipseCurve, Euler, Matrix3, Matrix4, Path, Shape, Vector2, Vector3 } from 'three';

import { PlatePair, PlatePlacement } from '@paragon/contract-library';
import { convertDegreesToRadians, Direction2D, Line2D, Point2D, Polygon2D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch, forceArray } from '@paragon/utilities';

import { newVector2 } from './newVector2';

const plateSlotWidth = 0.125;
export const plateSlotLength = 0.5;
const plateSlotStagger = 0.125;
const smh18PlateSlotStagger = 0.25;

export function buildPlateShape(
  plateLike: Pick<PlatePair, 'width' | 'length' | 'plateType' | 'geometryType' | 'hingeAngle'>,
  options?: {
    readonly slots?: boolean /* defaults to true */;
  },
): Shape | Shape[] {
  const plateShapeWithoutSlots = platePairToPolygonLocalCoordinates(plateLike);
  return options?.slots !== false
    ? buildPlateShapeWithSlots(plateLike, plateShapeWithoutSlots)
    : plateShapeWithoutSlots;
}

function platePairToPolygonLocalCoordinates(
  plateLike: Pick<PlatePair, 'width' | 'length' | 'geometryType' | 'plateType' | 'hingeAngle'>,
): Shape | Shape[] {
  switch (plateLike.geometryType) {
    case 'BevelPlateLeftHanded':
    case 'BevelPlateRightHanded':
      return new Shape(buildBevelPlateVertices(plateLike));
    case 'Hinged':
      return buildHingePlateShape(plateLike, false);
    case 'Rectangular':
      return new Shape([
        newVector2({ x: 0, y: 0 }),
        newVector2({ x: 0, y: plateLike.width }),
        newVector2({ x: plateLike.length, y: plateLike.width }),
        newVector2({ x: plateLike.length, y: 0 }),
      ]);
    case 'TPlate':
      return new Shape(buildTPlateVertices(plateLike));
    default:
      assertExhaustiveSwitch(plateLike.geometryType);
  }
}

function buildSlotVertices(basePoint: { x: number; y: number }, width: number, length: number): Vector2[] {
  const bottomLeft = new Vector2(basePoint.x, basePoint.y);
  const bottomRight = new Vector2(basePoint.x + length, basePoint.y);
  const topRight = new Vector2(basePoint.x + length, basePoint.y + width);
  const topLeft = new Vector2(basePoint.x, basePoint.y + width);

  return [bottomLeft, bottomRight, topRight, topLeft];
}

export function buildPlateShapeWithSlots(
  plateLike: Pick<PlatePair, 'width' | 'length' | 'plateType' | 'geometryType' | 'hingeAngle'>,
  plateShapeWithoutSlots: Shape | Shape[],
): Shape | Shape[] {
  if (plateLike.plateType === 'MTH18' || plateLike.plateType === 'SMH18') {
    return buildHingePlateShape(plateLike, true);
  }

  if (Array.isArray(plateShapeWithoutSlots)) throw new Error('Expected single Shape for non-hinge plate');
  const shape = new Shape(plateShapeWithoutSlots.getPoints());

  const slotLength = plateSlotLength;
  const slotWidth = plateSlotWidth;

  const lengthSlotCount = Math.floor(plateLike.length / slotLength / 2);
  const lengthSlotsDistance = (lengthSlotCount * 2 - 1) * slotLength;
  const lengthMargin = (plateLike.length - lengthSlotsDistance) / 2;

  const widthSlotCount = Math.floor(plateLike.width / slotWidth / 2);
  const widthSlotsDistance = (widthSlotCount * 2 - 1) * slotWidth;
  const widthMargin = (plateLike.width - widthSlotsDistance) / 2;

  for (let i = 0; i < lengthSlotCount; i += 1) {
    for (let j = 0; j < widthSlotCount; j += 1) {
      const xShift = ((j % 2 === 0 ? 1 : -1) * plateSlotStagger) / 2;
      const slotPosition = {
        x: lengthMargin + xShift + i * slotLength * 2,
        y: widthMargin + j * slotWidth * 2,
      };
      const slotVertices = buildSlotVertices(slotPosition, slotWidth, slotLength);

      if (plateLike.geometryType !== 'Rectangular' && !checkInside(slotVertices, shape.getPoints())) {
        continue;
      }

      shape.holes.push(new Path(slotVertices));
    }
  }

  return shape;
}

function buildHingePlateShape(
  plateLike: Pick<PlatePair, 'width' | 'length' | 'plateType' | 'geometryType' | 'hingeAngle'>,
  withSlots: boolean,
): Shape | Shape[] {
  if (plateLike.geometryType !== 'Hinged') throw new Error('Expected geometryType of Hinged for hinge plates');
  switch (plateLike.plateType) {
    case 'MTH18':
      return buildMTH18PlateShape(plateLike, withSlots);
    case 'SMH18':
      return buildSMH18PlateShape(plateLike, withSlots);
    default:
      throw new Error(`Unsupported plate type for hinge plate shape: ${plateLike.plateType}`);
  }
}

function buildHingePlateSides(
  plateLike: Pick<PlatePair, 'width' | 'length' | 'hingeAngle'>,
  hingeHoleRadius: number,
  marginOnOtherSideOfHinge: number,
): { readonly sideOneShape: Shape; readonly sideTwoShape: Shape; readonly sideOneTransformation: Matrix3 } {
  const sideTwoLeft = plateLike.length / 2 - hingeHoleRadius / 2 - marginOnOtherSideOfHinge;
  const translation = new Vector2(plateLike.length / 2, plateLike.width / 2);
  if (plateLike.hingeAngle === null) throw new Error('Hinge angle is required for hinge plates');
  const sideOneTransformation = new Matrix3()
    .makeTranslation(translation.x, translation.y)
    .multiply(new Matrix3().makeRotation(convertDegreesToRadians(plateLike.hingeAngle)))
    .multiply(new Matrix3().makeTranslation(-translation.x, -translation.y));

  const sideTwoShape = new Shape([
    new Vector2(sideTwoLeft, 0),
    new Vector2(plateLike.length, 0),
    new Vector2(plateLike.length, plateLike.width),
    new Vector2(sideTwoLeft, plateLike.width),
  ]);

  const sideOneShapePoints = sideTwoShape.getPoints().map((point) => point.applyMatrix3(sideOneTransformation));
  const sideOneShape = new Shape(sideOneShapePoints);

  return { sideOneShape, sideTwoShape, sideOneTransformation };
}

function addHingeHole(shapes: Shape[], plateLike: Pick<PlatePair, 'width' | 'length'>, radius: number) {
  const hingeHole = new Path(new EllipseCurve(plateLike.length / 2, plateLike.width / 2, radius, radius).getPoints(64));
  for (const shape of shapes) {
    shape.holes.push(hingeHole);
  }
}

/**
 * See https://icc-es.org/wp-content/uploads/report-directory/ESR-1988.pdf#page=7
 */
export const mth18HingeHoleRadius = 0.5;

/**
 * See https://icc-es.org/wp-content/uploads/report-directory/ESR-1988.pdf#page=7
 */
const mth18MarginOnOtherSideOfHinge = 0.5;

/**
 * Alternating rows of 3 and 2 slots, parallel to the width rather than the length. Slots occur on the outer 3 inches of
 * each side with a 1 inch diameter hole in the center for the hinge mechanism.
 *
 * See geometry details at https://icc-es.org/wp-content/uploads/report-directory/ESR-1988.pdf#page=7.
 */
function buildMTH18PlateShape(
  plateLike: Pick<PlatePair, 'width' | 'length' | 'geometryType' | 'hingeAngle'>,
  withSlots: boolean,
): Shape[] {
  const { sideOneShape, sideTwoShape, sideOneTransformation } = buildHingePlateSides(
    plateLike,
    mth18HingeHoleRadius,
    mth18MarginOnOtherSideOfHinge,
  );

  if (!withSlots) return [sideOneShape, sideTwoShape];

  const rowsOfTeethPerSide = 12;
  const inchesOnEachSideWithTeeth = 3;
  for (let rowNumber = 0; rowNumber < rowsOfTeethPerSide; rowNumber += 1) {
    const numberOfSlotsOnThisRow = rowNumber % 2 === 0 ? 3 : 2;
    const nonSlotXDistance = inchesOnEachSideWithTeeth - rowsOfTeethPerSide * plateSlotWidth;
    const xSpacing = nonSlotXDistance / rowsOfTeethPerSide;
    const rowX = xSpacing * (rowNumber + 0.5) + rowNumber * plateSlotWidth;
    const nonSlotYDistance = plateLike.width - numberOfSlotsOnThisRow * plateSlotLength;
    const ySpacing = nonSlotYDistance / (numberOfSlotsOnThisRow + 1);

    for (let slotNumber = 0; slotNumber < numberOfSlotsOnThisRow; slotNumber += 1) {
      const yPosition = ySpacing * (slotNumber + 1) + slotNumber * plateSlotLength;

      const sideTwoSlot = new Path(
        buildSlotVertices(
          {
            x: plateLike.length - rowX - plateSlotWidth,
            y: yPosition,
          },
          plateSlotLength,
          plateSlotWidth,
        ),
      );
      sideTwoShape.holes.push(sideTwoSlot);
      sideOneShape.holes.push(
        new Path(sideTwoSlot.getPoints().map((point) => point.applyMatrix3(sideOneTransformation))),
      );
    }
  }

  addHingeHole([sideOneShape, sideTwoShape], plateLike, mth18HingeHoleRadius);

  return [sideOneShape, sideTwoShape];
}

/**
 * See https://icc-es.org/wp-content/uploads/report-directory/ESR-1988.pdf#page=7
 */
export const smh18HingeHoleRadius = 0.375;

/**
 * See https://icc-es.org/wp-content/uploads/report-directory/ESR-1988.pdf#page=7
 */
const smh18MarginOnOtherSideOfHinge = 0.375;

export function buildSMH18PlateShape(
  plateLike: Pick<PlatePair, 'width' | 'length' | 'geometryType' | 'hingeAngle'>,
  withSlots: boolean,
): Shape[] {
  const { sideOneShape, sideTwoShape, sideOneTransformation } = buildHingePlateSides(
    plateLike,
    smh18HingeHoleRadius,
    smh18MarginOnOtherSideOfHinge,
  );

  if (!withSlots) return [sideOneShape, sideTwoShape];

  const inchesOnEachSideWithTeeth = 2;
  const lengthSlotCountPerSide = 8;
  const lengthSlotsDistance = (lengthSlotCountPerSide * 2 - 1) * plateSlotWidth;
  const lengthMargin = (inchesOnEachSideWithTeeth - lengthSlotsDistance) / 2;

  const widthSlotCount = 2;
  const widthSlotsDistance = (widthSlotCount * 2 - 1) * plateSlotLength;
  const widthMargin = (plateLike.width - widthSlotsDistance) / 2;

  for (let i = 0; i < lengthSlotCountPerSide; i += 1) {
    const yShift = ((i % 2 === 0 ? 1 : -1) * smh18PlateSlotStagger) / 2;
    for (let j = 0; j < widthSlotCount; j += 1) {
      const slotPositionLeftSide = {
        x: lengthMargin + i * plateSlotWidth * 2,
        y: widthMargin + yShift + j * plateSlotLength * 2,
      };
      sideOneShape.holes.push(
        new Path(
          buildSlotVertices(slotPositionLeftSide, plateSlotLength, plateSlotWidth).map((point) =>
            point.applyMatrix3(sideOneTransformation),
          ),
        ),
      );
      const slotPositionRightSide = {
        x: plateLike.length - plateSlotWidth - (lengthMargin + i * plateSlotWidth * 2),
        y: widthMargin - yShift + j * plateSlotLength * 2,
      };
      sideTwoShape.holes.push(new Path(buildSlotVertices(slotPositionRightSide, plateSlotLength, plateSlotWidth)));
    }
  }

  addHingeHole([sideOneShape, sideTwoShape], plateLike, smh18HingeHoleRadius);

  return [sideOneShape, sideTwoShape];
}

function checkInside(points: Vector2[], polygon: Vector2[]): boolean {
  return points.every((point) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x,
        yi = polygon[i].y;
      const xj = polygon[j].x,
        yj = polygon[j].y;

      const intersect = yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  });
}

function buildTPlateVertices(platePair: Pick<PlatePair, 'width' | 'length' | 'geometryType'>): Vector2[] {
  const { width, length } = platePair;
  if (width < 4 || length < 5) throw new Error('Invalid T-plate dimensions');

  return [
    newVector2({ x: 0, y: width - 3 }),
    newVector2({ x: 2, y: width - 3 }),
    newVector2({ x: 2, y: 0 }),
    newVector2({ x: length - 2, y: 0 }),
    newVector2({ x: length - 2, y: width - 3 }),
    newVector2({ x: length, y: width - 3 }),
    newVector2({ x: length, y: width }),
    newVector2({ x: 0, y: width }),
  ];
}

function buildBevelPlateVertices(plateLike: Pick<PlatePair, 'width' | 'length' | 'geometryType'>): Vector2[] {
  const bottomLeft = new Point2D(0, 0);
  const bottomRight = new Point2D(plateLike.length, 0);
  const topRight = new Point2D(plateLike.length, plateLike.width);
  const topLeft = new Point2D(0, plateLike.width);
  const rectangle = new Polygon2D([bottomLeft, bottomRight, topRight, topLeft]);
  const sliceForLeft = plateLike.geometryType === 'BevelPlateLeftHanded';
  const slicePoint = sliceForLeft ? topRight : bottomRight;
  const sliceDirection = Direction2D.fromAngle(sliceForLeft ? 45 : -45);
  const sliceLine = new Line2D(slicePoint, sliceDirection);
  const bevelPlatePolygon = maxBy(rectangle.slice(sliceLine), (polygon) => polygon.area);
  return bevelPlatePolygon!.vertices.map(newVector2);
}

export function getPlatePairPolygonVertices2D(platePair: PlatePair) {
  return platePairToPolygonsWorldCoordinates(platePair).map((polygon) => polygon.map(Point2D.fromObject));
}

export const platePairToPolygonsWorldCoordinates = (platePair: PlatePair): readonly Vector3[][] => {
  const platePairLocalPolygon = platePairToPolygonLocalCoordinates(platePair);
  const platePolygons = forceArray(platePairLocalPolygon).map((shape) =>
    shape.getPoints().map((vertex) => new Vector3(vertex.x, vertex.y, 0)),
  );
  const plateMatrix = getPlatePairPlate1Matrix(platePair);
  for (const platePolygon of platePolygons) {
    for (const vertex of platePolygon) {
      vertex.applyMatrix4(plateMatrix);
    }
  }
  return platePolygons;
};

export const getPlatePairCenterMatrix = (platePair: PlatePair) => {
  const eulerRotation = new Euler(
    convertDegreesToRadians(platePair.angleX),
    convertDegreesToRadians(platePair.angleY),
    convertDegreesToRadians(platePair.angleZ),
  );
  return new Matrix4()
    .makeTranslation(platePair.centerX, platePair.centerY, platePair.centerZ)
    .multiply(new Matrix4().makeRotationFromEuler(eulerRotation))
    .multiply(new Matrix4().makeTranslation(-platePair.length / 2, -platePair.width / 2, 0));
};

export const getPlatePairPlate2Matrix = (platePair: PlatePair) => {
  const thickness = getPlateThicknessForVisibility(platePair.platePlacement);
  return new Matrix4()
    .makeTranslation(0, 0, -platePair.distanceBetweenPlates / 2 - thickness)
    .premultiply(getPlatePairCenterMatrix(platePair));
};

export const getPlatePairPlate1Matrix = (platePair: PlatePair) =>
  new Matrix4()
    .makeTranslation(0, 0, platePair.distanceBetweenPlates / 2)
    .premultiply(getPlatePairCenterMatrix(platePair));

const defaultPlateThickness = 0.0356; // Total plate thickness for a twenty gauge plate
export const getPlateThicknessForVisibility = (platePlacement: PlatePlacement) =>
  platePlacement === 'TopBottom' ? defaultPlateThickness * 5 : defaultPlateThickness;
